import React, { useState } from 'react';
import { saveAs } from 'file-saver';
import { Tooltip } from '@mui/material';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import SyntaxHighlighter, { editorTheme, normaliseLanguage, languageLabel } from './code/highlighter';

export const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (e) {
    // Fallback for non-secure contexts
    const area = document.createElement('textarea');
    area.value = text;
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(area);
    return ok;
  }
};

export const CopyButton = ({ text, label = 'Copy', className = 'code-action' }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    if (await copyText(text)) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    }
  };
  return (
    <button type="button" className={className} onClick={handleCopy} aria-label={copied ? 'Copied' : 'Copy code'}>
      {copied ? <CheckRoundedIcon sx={{ fontSize: 15 }} /> : <ContentCopyRoundedIcon sx={{ fontSize: 15 }} />}
      <span>{copied ? 'Copied!' : label}</span>
    </button>
  );
};

export const HighlightedCode = ({ code, language, showLineNumbers = true, style }) => (
  <SyntaxHighlighter
    language={language || 'text'}
    style={editorTheme}
    showLineNumbers={showLineNumbers}
    wrapLongLines={false}
    lineNumberStyle={{ minWidth: '2.5em', paddingRight: '1em', color: '#6e7681', userSelect: 'none' }}
    customStyle={{ margin: 0, padding: '16px 0 16px 6px', background: '#1e1e1e', fontSize: '14px', lineHeight: 1.6, ...style }}
    // Ligatures off: code must show the exact characters (">=" not "⩾", "~>" not an arrow)
    codeTagProps={{ style: { fontFamily: "'JetBrains Mono', Consolas, 'Courier New', monospace", fontVariantLigatures: 'none', fontFeatureSettings: '"liga" 0, "calt" 0' } }}
  >
    {code}
  </SyntaxHighlighter>
);

// Editor-style code block used inside chat answers
const CodeBlock = ({ code, language, fileName }) => {
  const lang = normaliseLanguage(language, fileName);
  const lines = code.split('\n').length;

  const handleDownload = () => {
    saveAs(new Blob([code.endsWith('\n') ? code : code + '\n'], { type: 'text/plain;charset=utf-8' }), fileName);
  };

  return (
    <div className="code-block">
      <div className="code-block-header">
        <span className="code-block-title">
          {fileName ? <span className="code-file-name">{fileName}</span> : null}
          <span className="code-lang">{languageLabel(lang)}</span>
        </span>
        <span className="code-block-actions">
          {fileName && (
            <Tooltip title={`Download ${fileName}`} arrow>
              <button type="button" className="code-action" onClick={handleDownload} aria-label={`Download ${fileName}`}>
                <FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />
              </button>
            </Tooltip>
          )}
          <CopyButton text={code} />
        </span>
      </div>
      <HighlightedCode code={code} language={lang} showLineNumbers={lines > 1} />
    </div>
  );
};

export default CodeBlock;
